import { TriggerOperator, isUnaryOperator, isListOperator } from '../chunk-LGUPHS47.js';
export { LIST_TRIGGER_OPERATORS, OPERATOR_ALIASES, TRIGGER_OPERATORS, TRIGGER_OPERATOR_LABELS, TriggerOperator, UNARY_TRIGGER_OPERATORS, isListOperator, isUnaryOperator, normalizeOperator, triggerOperatorLabel } from '../chunk-LGUPHS47.js';
import { TERMINUS_CLASSES } from '../chunk-VK4UBDGG.js';
import { recordedConfidence } from '../chunk-X34AGPO3.js';
import { fieldLabel } from '../chunk-NHT4QHRP.js';
import '../chunk-HKIGNAVO.js';
import '../chunk-GPJ572YS.js';
import '../chunk-N7SZ66QO.js';
import '../chunk-PZ5AY32C.js';

// src/triggers/fields.ts
var FINALITY_STATES = ["observed", "confirmed", "finalized", "reorged"];
var EVENT_KINDS = ["log", "internal_tx", "native_transfer"];
var SIGNAL_KINDS = [
  "pool_opened",
  "liquidity_front_run",
  "liquidity_rug",
  "mev_sandwich",
  "token_insider_buy",
  "group_completed",
  "risk_threshold",
  "peel_chain",
  // Not a detector of its own: the projection of the four launch findings onto
  // the ACCUSED sender's transactions only (see `culprit_tx_ids`), so a rule can
  // follow a wallet the way the Actors board counts it.
  "actor_alert",
  // A wallet's cheap, late, winning buys on a prediction market, raised when the
  // market resolves (`context_store.strategies.prediction_market`).
  "prediction_informed_entry",
  // The same settlement's other findings: several wallets' split cheap entry, the
  // market's own authority trading it, and — from the per-wallet record it files
  // (`context_store.strategies.prediction_record`) — a track record too good for
  // its prices and copy trading.
  "prediction_coordinated_entry",
  "prediction_oracle_trade",
  "prediction_improbable_record",
  "prediction_copy_trading",
  // Two wallets trading volume back and forth with each other, keeping nothing.
  "prediction_wash_trade",
  // A combination of findings about one actor (`commons.risk_patterns`).
  "risk_pattern",
  // Spam findings: one sender dusting many wallets, and lookalike transfers
  // imitating a genuine counterparty (`context_store.strategies.dust_campaign`,
  // `address_poisoning`). Both are journaled findings with situation defs.
  "dust_campaign",
  "address_poisoning",
  // Token lifecycle and grouping signals: a deployment, a launch, a sniped
  // launch, a saga that timed out, and a prediction market's settlement.
  "token_deployed",
  "token_launched",
  "token_sniped",
  "group_timed_out",
  "prediction_settled"
];
var ENRICHMENT_KINDS = [
  "risk_score",
  "tx_value_usd",
  "price",
  "label",
  "tag",
  "entity",
  "group",
  "classification",
  "protocol_classification",
  // Evaluation-time observations from the shared asset-leg/custody projection.
  "fund_movement",
  // Cross-transaction findings, projected onto the envelope by
  // `ContextSignalProjectStep` (see SIGNAL_KINDS below).
  ...SIGNAL_KINDS
];
var RISK_BUCKETS = ["none", "low", "medium", "high", "critical"];
var O = TriggerOperator;
var STRING_OPS = [O.EQ, O.NE, O.MATCHES, O.IN, O.NOT_IN, O.EXISTS];
var ENUM_OPS = [O.EQ, O.NE, O.IN, O.NOT_IN, O.EXISTS];
var NUMBER_OPS = [O.EQ, O.NE, O.GT, O.GTE, O.LT, O.LTE, O.IN, O.NOT_IN, O.EXISTS];
var TIME_OPS = [O.EQ, O.NE, O.GT, O.GTE, O.LT, O.LTE, O.EXISTS];
var BOOL_OPS = [O.EQ, O.NE, O.EXISTS];
var OPERATORS_BY_TYPE = {
  string: STRING_OPS,
  hash: STRING_OPS,
  address: STRING_OPS,
  enum: ENUM_OPS,
  number: NUMBER_OPS,
  timestamp: TIME_OPS,
  boolean: BOOL_OPS
};
function f(path, group, valueType, label, extra) {
  return {
    path,
    group,
    valueType,
    label,
    operators: extra?.operators ?? OPERATORS_BY_TYPE[valueType],
    enumValues: extra?.enumValues,
    description: extra?.description
  };
}
var TRIGGER_FIELDS = [
  // --- canonical transaction (RuleEvaluator `canonical.<attr>`) -----------
  f("canonical.chain_id", "transaction", "string", fieldLabel("chain_id"), {
    description: "CAIP-2 id, e.g. eip155:1. Normalised on both sides."
  }),
  f("canonical.tx_hash", "transaction", "hash", fieldLabel("tx_hash")),
  f("canonical.from_address", "transaction", "address", fieldLabel("from_address"), {
    description: "Sender. Lower-cased; compare against the plain address."
  }),
  f("canonical.to_address", "transaction", "address", fieldLabel("to_address"), {
    description: "Recipient. Absent for a contract creation, so `exists` is meaningful."
  }),
  f("canonical.participant_address", "transaction", "address", "Any participant address", {
    description: "Matches any recorded participant on the transaction's chain, including token recipients. Negation excludes matching participants only when wrapped in NOT."
  }),
  f("canonical.value", "transaction", "number", "Value (native)", {
    description: "Native value as a decimal string."
  }),
  f("canonical.value_usd", "transaction", "number", fieldLabel("value_usd"), {
    description: "Pulled from the tx_value_usd enrichment's total_usd."
  }),
  f("canonical.finality", "transaction", "enum", fieldLabel("finality"), {
    enumValues: FINALITY_STATES
  }),
  f("canonical.block_number", "transaction", "number", fieldLabel("block_number")),
  f("canonical.block_timestamp", "transaction", "timestamp", fieldLabel("block_timestamp")),
  f("canonical.input_data", "transaction", "string", "Input Data"),
  // --- events (RuleEvaluator `event.<attr>`) ------------------------------
  f("event.kind", "event", "enum", "Event Kind", { enumValues: EVENT_KINDS }),
  // --- any-enrichment matcher (RuleEvaluator `enrichment.kind`) -----------
  f("enrichment.kind", "enrichment", "enum", "Enrichment Kind", {
    enumValues: ENRICHMENT_KINDS,
    description: "Matches when any enrichment of this kind is present."
  }),
  // --- risk (`risk_score.<path>`) ----------------------------------------
  f("risk_score.score", "risk", "number", "Risk Score", {
    description: "0\u2013100 weighted-sum score from the risk engine."
  }),
  f("risk_score.bucket", "risk", "enum", "Risk Bucket", { enumValues: RISK_BUCKETS }),
  f("risk_score.policy_id", "risk", "string", "Risk Policy"),
  // Observed transfers: related predicates must use one enrichment scope.
  f("fund_movement.from_address", "enrichment", "address", "Movement sender"),
  f("fund_movement.to_address", "enrichment", "address", "Movement recipient"),
  f("fund_movement.token", "enrichment", "address", "Movement token contract or native"),
  f("fund_movement.amount_raw", "enrichment", "string", "Movement amount (base units)"),
  f("fund_movement.protocol_id", "protocol", "string", "Movement transaction protocol"),
  f("fund_movement.destination_class", "catalog", "string", "Movement destination class"),
  f("fund_movement.destination_entity_id", "catalog", "string", "Movement destination entity"),
  f("fund_movement.attribution_source", "catalog", "enum", "Movement attribution source", {
    enumValues: ["registry", "catalog"]
  }),
  // --- value (`tx_value_usd` / `price` enrichments) -----------------------
  f("tx_value_usd.total_usd", "enrichment", "number", "Value (USD)"),
  f("price.value_usd", "enrichment", "number", "Price (USD)"),
  // --- catalog hits (`<kind>.catalog_id`) ---------------------------------
  f("tag.catalog_id", "catalog", "string", "Tag (catalog id)"),
  f("label.catalog_id", "catalog", "string", "Label (catalog id)"),
  f("entity.catalog_id", "catalog", "string", "Entity (catalog id)"),
  f("group.catalog_id", "catalog", "string", "Group (catalog id)"),
  // --- protocol attribution (`protocol_detect` step) ----------------------
  f("protocol_classification.primary_protocol_id", "protocol", "string", "Protocol", {
    description: "The protocol this transaction is attributed to, e.g. chainlink_ccip, uniswap_v3."
  }),
  f("protocol_classification.is_fallback_primary", "protocol", "boolean", "Protocol Is Guess", {
    description: "True when the attribution came from a fallback rather than a confident match."
  }),
  // --- cross-transaction signals (`ContextSignalProjectStep`) -------------
  // Every signal also carries `key`, `chain_id` and `refs`, merged in by the
  // projection step; only the payload fields worth alerting on are listed.
  f("pool_opened.protocol_id", "signal", "string", "New Pool: Protocol"),
  f("pool_opened.creator", "signal", "address", "New Pool: Creator"),
  f("pool_opened.identity", "signal", "address", "New Pool: Address"),
  f("pool_opened.instance_kind", "signal", "string", "New Pool: Instance Kind"),
  f("liquidity_rug.provider", "signal", "address", "Rug: Liquidity Provider"),
  f("liquidity_rug.removed_share", "signal", "number", "Rug: Share Removed", {
    description: "0\u20131 fraction of pool liquidity pulled."
  }),
  f("liquidity_rug.is_creator", "signal", "boolean", "Rug: Puller Is Creator"),
  f("liquidity_rug.blocks_after_open", "signal", "number", "Rug: Blocks After Open"),
  f("liquidity_front_run.provider", "signal", "address", "Front-run: Provider"),
  f("mev_sandwich.bot_address", "signal", "address", "Sandwich: Bot"),
  f("mev_sandwich.pool", "signal", "string", "Sandwich: Pool"),
  f("mev_sandwich.net_profit_wei", "signal", "number", "Sandwich: Net Profit (wei)"),
  f("token_insider_buy.launch_token", "signal", "address", "Insider Buy: Token"),
  f("token_insider_buy.deployer", "signal", "address", "Insider Buy: Deployer"),
  f("token_insider_buy.deployer_bought", "signal", "boolean", "Insider Buy: Deployer Bought", {
    description: "True when the account that created the token bought it. False means a liquidity provider did \u2014 a weaker claim."
  }),
  f("token_insider_buy.buy_count", "signal", "number", "Insider Buy: Buys"),
  f("token_insider_buy.launch_pool", "signal", "address", "Insider Buy: Pool"),
  f("actor_alert.actor", "signal", "address", "Accused Wallet", {
    description: "The wallet a launch finding names as the one that did it \u2014 never a victim or a bystander the finding merely includes."
  }),
  f("actor_alert.alert", "signal", "string", "Accused Of", {
    description: "liquidity_rug, liquidity_front_run, mev_sandwich or token_insider_buy."
  }),
  f("group_completed.member_count", "signal", "number", "Group Completed: Members", {
    description: "Fires when a multi-leg saga (e.g. a CCIP bridge) has all its legs."
  }),
  f("risk_threshold.score", "signal", "number", "Risk Threshold: Score"),
  f("risk_threshold.band", "signal", "string", "Risk Threshold: Band"),
  // A reconstructed forwarding path that ended somewhere the trail stops. Every
  // link is an amount correlation rather than a shared identifier, so
  // `chain_confidence` is offered alongside the rest: a rule that matches any
  // three-hop amount coincidence is a rule somebody will learn to ignore.
  // Mirrors `commons.chains.custody.CustodyClass` through the flow model's
  // `TERMINUS_CLASSES` — the one FE copy of that vocabulary. The *situation*
  // picker never reads this list: `flow.peel_chain` derives its `enum_values`
  // from `TERMINAL_ENTITY_TYPES` and serves them from `GET /v0/situations`.
  // This copy exists for the raw rule builder, where a stale value costs an
  // author one option rather than silently breaking an authored watch.
  f("peel_chain.terminus_class", "signal", "enum", "Peel Chain: Ended At", {
    enumValues: [...TERMINUS_CLASSES],
    description: "What kind of place the trail stopped at."
  }),
  f("peel_chain.terminus_entity_id", "signal", "string", "Peel Chain: Venue", {
    description: "The venue the funds reached, e.g. `kucoin`. Stamped for every terminus class; `peel_chain.exchange_id` carries the same value but only for exchanges."
  }),
  f("peel_chain.deposit_wallet", "signal", "address", "Peel Chain: Depositing Wallet", {
    description: "The last non-custodial hop \u2014 the wallet that paid the venue."
  }),
  f("peel_chain.origin_wallet", "signal", "address", "Peel Chain: Origin Wallet", {
    description: "As far back as the ingested transactions allowed, which is not necessarily the true source."
  }),
  f("peel_chain.hop_count", "signal", "number", "Peel Chain: Hops", {
    description: "A direct deposit is one hop."
  }),
  f("peel_chain.chain_confidence", "signal", "number", "Peel Chain: Confidence", {
    description: "0\u20131. Decays with chain length and with weaker hop shapes; a chain of clean sweeps scores highest."
  }),
  f("peel_chain.token", "signal", "string", "Peel Chain: Token", {
    description: "`native` for the chain's own coin, else the token address."
  }),
  f("prediction_informed_entry.trader", "signal", "address", "Informed Entry: Wallet", {
    description: "The order's maker \u2014 on Polymarket, the trader's proxy wallet."
  }),
  f("prediction_informed_entry.condition_id", "signal", "hash", "Informed Entry: Market", {
    description: "The market's CTF condition id."
  }),
  f("prediction_informed_entry.profit_usdc", "signal", "number", "Informed Entry: Profit ($)", {
    description: "What the cheap entries paid out at resolution, less what they cost."
  }),
  f("prediction_informed_entry.avg_price", "signal", "number", "Informed Entry: Entry Price", {
    description: "Average price paid per share, 0\u20131 \u2014 the odds the market gave the outcome."
  }),
  f("prediction_informed_entry.lead_hours", "signal", "number", "Informed Entry: Hours Before Resolution", {
    description: "How long before the market resolved the last cheap buy landed."
  }),
  f("prediction_informed_entry.cost_usdc", "signal", "number", "Informed Entry: Staked ($)")
];
var TRIGGER_FIELD_BY_PATH = new Map(
  TRIGGER_FIELDS.map((field) => [field.path, field])
);
var TRIGGER_FIELD_GROUP_LABELS = {
  transaction: "Transaction",
  risk: "Risk",
  enrichment: "Enrichment",
  event: "Event",
  catalog: "Catalog",
  protocol: "Protocol",
  signal: "Cross-transaction signals"
};
function getTriggerField(path) {
  return TRIGGER_FIELD_BY_PATH.get(path);
}
function triggerFieldLabel(path) {
  const field = TRIGGER_FIELD_BY_PATH.get(path);
  if (field) return field.label;
  const leaf = path.includes(".") ? path.slice(path.lastIndexOf(".") + 1) : path;
  return fieldLabel(leaf);
}
function operatorsForField(path) {
  return getTriggerField(path)?.operators ?? OPERATORS_BY_TYPE.string;
}
function triggerFieldOptions() {
  const order = [
    "transaction",
    "risk",
    "enrichment",
    "event",
    "catalog",
    "protocol",
    "signal"
  ];
  return order.map((group) => ({
    group,
    label: TRIGGER_FIELD_GROUP_LABELS[group],
    fields: TRIGGER_FIELDS.filter((field) => field.group === group)
  })).filter((entry) => entry.fields.length > 0);
}

// src/triggers/envelope.ts
function collectEnrichments(envelope, opts) {
  const out = [];
  for (const layer of envelope.enrichment_layers ?? []) {
    for (const e of layer.enrichments) {
      if (opts?.kind != null && e.kind !== opts.kind) continue;
      if (opts?.scope != null && String(e.payload?.scope) !== opts.scope) continue;
      if (opts?.minConfidence != null) {
        const score = recordedConfidence(e);
        if (score == null || score < opts.minConfidence) continue;
      }
      out.push(e);
    }
  }
  return out;
}

// src/triggers/rule.ts
var LogicalOp = {
  AND: "and",
  OR: "or",
  NOT: "not"
};
function predicate(field, op, value) {
  return { field, op, value };
}
function predicateLeaf(p) {
  return { predicate: p };
}
function and(...children) {
  return { op: LogicalOp.AND, children };
}
function or(...children) {
  return { op: LogicalOp.OR, children };
}
function not(...children) {
  return { op: LogicalOp.NOT, children };
}
function anyCondition() {
  return {};
}
function conditionFromPredicates(predicates, match) {
  if (predicates.length === 0) return anyCondition();
  if (predicates.length === 1) return predicateLeaf(predicates[0]);
  const children = predicates.map(predicateLeaf);
  return match === "all" ? and(...children) : or(...children);
}
function flattenCondition(condition) {
  if (!condition || condition.op == null && condition.predicate == null) {
    return { predicates: [], match: "all" };
  }
  if (condition.predicate != null) {
    return { predicates: [condition.predicate], match: "all" };
  }
  if (condition.op === LogicalOp.AND || condition.op === LogicalOp.OR) {
    const children = condition.children ?? [];
    const predicates = [];
    for (const child of children) {
      if (child.predicate == null) return null;
      predicates.push(child.predicate);
    }
    return { predicates, match: condition.op === LogicalOp.AND ? "all" : "any" };
  }
  return null;
}
function emptyTriggerRule(id) {
  return {
    id,
    name: "",
    enabled: true,
    condition: anyCondition(),
    actions: [],
    cooldown_seconds: 0,
    metadata: {}
  };
}

// src/triggers/actions.ts
var TriggerActionType = {
  NOTIFY: "notify",
  START_WORKFLOW: "start_workflow",
  INTEGRATION: "integration"
};
var TRIGGER_ACTION_TYPES = [
  TriggerActionType.NOTIFY,
  TriggerActionType.START_WORKFLOW,
  TriggerActionType.INTEGRATION
];
var TRIGGER_ACTION_TYPE_LABELS = {
  notify: "Alert / notify",
  start_workflow: "Start workflow",
  integration: "Integration"
};
function notifyAction(destinationId) {
  return { type: TriggerActionType.NOTIFY, destination_id: destinationId };
}
function startWorkflowAction(workflowId, input) {
  return {
    type: TriggerActionType.START_WORKFLOW,
    workflow_id: workflowId,
    ...input ? { input } : {}
  };
}
function integrationAction(connectorId, config) {
  return {
    type: TriggerActionType.INTEGRATION,
    connector_id: connectorId,
    ...config ? { config } : {}
  };
}
function isNotifyAction(a) {
  return a.type === TriggerActionType.NOTIFY;
}
function isStartWorkflowAction(a) {
  return a.type === TriggerActionType.START_WORKFLOW;
}
function isIntegrationAction(a) {
  return a.type === TriggerActionType.INTEGRATION;
}
function asRecord(value) {
  return value && typeof value === "object" && !Array.isArray(value) ? value : void 0;
}
function parseTriggerAction(record) {
  const type = typeof record.type === "string" ? record.type : void 0;
  if (type === TriggerActionType.NOTIFY || type === "destination") {
    return typeof record.destination_id === "string" ? { type: TriggerActionType.NOTIFY, destination_id: record.destination_id } : null;
  }
  if (type === TriggerActionType.START_WORKFLOW) {
    if (typeof record.workflow_id !== "string") return null;
    const action = {
      type: TriggerActionType.START_WORKFLOW,
      workflow_id: record.workflow_id
    };
    const input = asRecord(record.input);
    if (input) action.input = input;
    if (record.mode === "async" || record.mode === "sync") action.mode = record.mode;
    return action;
  }
  if (type === TriggerActionType.INTEGRATION) {
    if (typeof record.connector_id !== "string") return null;
    const action = {
      type: TriggerActionType.INTEGRATION,
      connector_id: record.connector_id
    };
    const config = asRecord(record.config);
    if (config) action.config = config;
    return action;
  }
  return null;
}
function triggerActionToRecord(action) {
  return { ...action };
}
var DEFAULT_CONNECTORS = [
  {
    id: "http",
    name: "Generic HTTP",
    description: "POST the matched event to a customer endpoint.",
    configFields: [{ key: "url", label: "Endpoint URL", type: "string", required: true }]
  },
  { id: "pagerduty", name: "PagerDuty", description: "Open a PagerDuty incident." },
  { id: "zapier", name: "Zapier", description: "Kick off a Zapier zap via catch hook." }
];
function getConnector(id) {
  return DEFAULT_CONNECTORS.find((c) => c.id === id);
}

// src/triggers/evaluate.ts
var EvalResult = class {
  matchedEnrichments = [];
  add(e) {
    const record = {
      kind: e.kind,
      source: e.source,
      confidence: recordedConfidence(e),
      confidence_status: recordedConfidence(e) == null ? "unscored" : "scored",
      payload: { ...e.payload }
    };
    const key = JSON.stringify(record);
    if (!this.matchedEnrichments.some((m) => JSON.stringify(m) === key)) {
      this.matchedEnrichments.push(record);
    }
  }
};
var FIELD_AT_SCOPE_RE = /^([a-zA-Z_]+)@([a-zA-Z_]+)(?:\.(.+))?$/;
var PY_FLOAT = /^[+-]?(?:\d[\d_]*(?:\.[\d_]*)?|\.[\d_]+)(?:[eE][+-]?\d[\d_]*)?$/;
var PY_INF_NAN = /^[+-]?(?:inf(?:inity)?|nan)$/i;
function toFloat(value) {
  if (typeof value === "number") return value;
  if (typeof value === "boolean") return value ? 1 : 0;
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (trimmed === "") return null;
    const special = PY_INF_NAN.exec(trimmed);
    if (special) {
      if (/nan$/i.test(trimmed)) return Number.NaN;
      return trimmed.startsWith("-") ? -Infinity : Infinity;
    }
    if (!PY_FLOAT.test(trimmed)) return null;
    const n = Number(trimmed.replace(/_/g, ""));
    return Number.isNaN(n) ? null : n;
  }
  return null;
}
function pyEq(a, b) {
  if (a === b) return true;
  if (a == null || b == null) return a == null && b == null;
  if (typeof a === "boolean" || typeof b === "boolean") {
    if (typeof a === "string" || typeof b === "string") return false;
    return Number(a) === Number(b);
  }
  if (typeof a === "number" && typeof b === "number") return a === b;
  if (typeof a === "string" && typeof b === "string") return a === b;
  if (typeof a !== typeof b) return false;
  return JSON.stringify(a) === JSON.stringify(b);
}
function membership(actual, expected) {
  if (Array.isArray(expected)) return expected.some((item) => pyEq(actual, item));
  if (typeof expected === "string" && typeof actual === "string") {
    return expected.includes(actual);
  }
  return false;
}
function compare(actual, op, expected) {
  switch (op) {
    case TriggerOperator.EXISTS:
      return actual !== void 0 && actual !== null;
    case TriggerOperator.EQ:
      return pyEq(actual, expected);
    case TriggerOperator.NE:
      return !pyEq(actual, expected);
    case TriggerOperator.IN:
      return membership(actual, expected);
    case TriggerOperator.NOT_IN:
      if (!Array.isArray(expected) && typeof expected !== "string") return true;
      return !membership(actual, expected);
    case TriggerOperator.MATCHES: {
      if (actual == null || typeof expected !== "string") return false;
      try {
        return new RegExp(expected).test(String(actual));
      } catch {
        return false;
      }
    }
    case TriggerOperator.GTE:
    case TriggerOperator.LTE:
    case TriggerOperator.GT:
    case TriggerOperator.LT: {
      const a = toFloat(actual);
      const b = toFloat(expected);
      if (a === null || b === null) return false;
      if (op === TriggerOperator.GTE) return a >= b;
      if (op === TriggerOperator.LTE) return a <= b;
      if (op === TriggerOperator.GT) return a > b;
      return a < b;
    }
    default:
      return false;
  }
}
function scalarize(value) {
  if (value == null || typeof value !== "object" || Array.isArray(value)) return value;
  const record = value;
  if (record.value != null && record.chain_id != null) return record.value;
  return value;
}
function getCanonicalField(envelope, attr) {
  const canonical = envelope.canonical;
  if (canonical == null) return void 0;
  if (attr === "value_usd") {
    for (const e of collectEnrichments(envelope, { kind: "tx_value_usd" })) {
      return e.payload?.total_usd;
    }
    return void 0;
  }
  let actual = canonical;
  for (const segment of attr.split(".")) {
    if (actual == null || typeof actual !== "object") return void 0;
    actual = actual[segment];
  }
  return scalarize(actual);
}
function getPayloadPath(payload, path) {
  let actual = payload;
  for (const segment of path.split(".")) {
    if (actual != null && typeof actual === "object" && !Array.isArray(actual)) {
      actual = actual[segment];
    } else {
      return void 0;
    }
  }
  return actual;
}
function evalPredicate(predicate2, envelope, result) {
  const { field, op, value, min_confidence: minConfidence } = predicate2;
  if (minConfidence != null && (!Number.isFinite(minConfidence) || minConfidence < 0 || minConfidence > 1 || field.startsWith("canonical.") || field.startsWith("event."))) return false;
  if (field === "canonical.participant_address") {
    const participants = envelope.canonical?.participants;
    return Array.isArray(participants) && participants.some((p) => p?.address?.chain_id === envelope.canonical?.chain_id && compare(scalarize(p?.address), op, value));
  }
  if (field.startsWith("canonical.")) {
    const attr = field.slice("canonical.".length);
    return compare(getCanonicalField(envelope, attr), op, value);
  }
  if (field.startsWith("event.")) {
    const attr = field.slice("event.".length);
    for (const ev of envelope.canonical?.events ?? []) {
      if (compare(ev[attr], op, value)) return true;
    }
    return false;
  }
  const at = FIELD_AT_SCOPE_RE.exec(field);
  if (at) {
    const [, kind, scope, rest] = at;
    for (const e of collectEnrichments(envelope, { kind, scope, minConfidence })) {
      if (rest == null) {
        result.add(e);
        return true;
      }
      if (compare(getPayloadPath(e.payload, rest), op, value)) {
        result.add(e);
        return true;
      }
    }
    return false;
  }
  if (field.includes(".")) {
    const idx = field.indexOf(".");
    const kind = field.slice(0, idx);
    const rest = field.slice(idx + 1);
    if (rest === "kind") {
      for (const e of collectEnrichments(envelope, { minConfidence })) {
        if (compare(e.kind, op, value)) {
          result.add(e);
          return true;
        }
      }
      return false;
    }
    for (const e of collectEnrichments(envelope, { kind, minConfidence })) {
      if (compare(getPayloadPath(e.payload, rest), op, value)) {
        result.add(e);
        return true;
      }
    }
    return false;
  }
  return false;
}
function evalCondition(condition, envelope, result) {
  if (condition.enrichment_kind) {
    const unbound = { ...condition, enrichment_kind: void 0 };
    let matched = false;
    for (const e of collectEnrichments(envelope, { kind: condition.enrichment_kind })) {
      const local2 = new EvalResult();
      if (evalCondition(unbound, { ...envelope, enrichment_layers: [{ enrichments: [e] }] }, local2)) {
        matched = true;
        result.matchedEnrichments.push(...local2.matchedEnrichments);
      }
    }
    return matched;
  }
  if (condition.predicate != null) return evalPredicate(condition.predicate, envelope, result);
  if (condition.op == null) return true;
  const children = condition.children ?? [];
  const local = new EvalResult();
  if (condition.op === "not") return !children.some((c) => evalCondition(c, envelope, new EvalResult()));
  if (condition.op === "and") {
    if (!children.every((c) => evalCondition(c, envelope, local))) return false;
    result.matchedEnrichments.push(...local.matchedEnrichments);
    return true;
  }
  if (condition.op === "or") {
    let matched = false;
    for (const c of children) {
      const branch = new EvalResult();
      if (evalCondition(c, envelope, branch)) {
        matched = true;
        result.matchedEnrichments.push(...branch.matchedEnrichments);
      }
    }
    return matched;
  }
  return false;
}
function evaluatePredicate(predicate2, envelope) {
  return evalPredicate(predicate2, envelope, new EvalResult());
}
function evaluateCondition(condition, envelope) {
  return evalCondition(condition, envelope, new EvalResult());
}
function evaluateRule(rule, envelope) {
  const condition = "condition" in rule ? rule.condition : rule;
  const result = new EvalResult();
  const matched = evalCondition(condition, envelope, result);
  return { matched, matchedEnrichments: result.matchedEnrichments };
}
var PREVIEW_BUDGET_MS = 250;
function previewRule(rule, envelopes, limit) {
  const matches = [];
  const deadline = Date.now() + PREVIEW_BUDGET_MS;
  for (const envelope of envelopes) {
    const evaluation = evaluateRule(rule, envelope);
    if (evaluation.matched) matches.push({ envelope, evaluation });
    if (limit && matches.length >= limit) break;
    if (Date.now() > deadline) break;
  }
  return matches;
}

// src/triggers/editing.ts
function valueText(value) {
  if (value == null) return "";
  if (Array.isArray(value)) return value.join(", ");
  return String(value);
}
function coerceValue(field, op, raw, authoredValue) {
  if (isUnaryOperator(op)) return void 0;
  const def = getTriggerField(field);
  const samples = Array.isArray(authoredValue) ? authoredValue : [authoredValue];
  const authoredType = samples.length && samples.every((v) => typeof v === typeof samples[0]) ? typeof samples[0] : void 0;
  const type = def?.valueType ?? authoredType;
  const isNumeric = type === "number" || type === "timestamp";
  const one = (s) => {
    const t = s.trim();
    if (type === "boolean" && (t === "true" || t === "false")) return t === "true";
    if (isNumeric && t !== "" && Number.isFinite(Number(t))) return Number(t);
    return t;
  };
  if (isListOperator(op)) {
    return raw.split(",").map((s) => s.trim()).filter((s) => s !== "").map(one);
  }
  return one(raw);
}
function isNumericField(field) {
  const type = getTriggerField(field)?.valueType;
  return type === "number" || type === "timestamp";
}
function hasNoValue(value) {
  const values = Array.isArray(value) ? value : [value];
  return values.length === 0 || values.every((v) => v == null || typeof v === "string" && v.trim() === "");
}
function predicateProblem(predicate2) {
  if (isUnaryOperator(predicate2.op)) return null;
  if (hasNoValue(predicate2.value)) {
    return {
      kind: "blank-value",
      tone: "hint",
      message: "No value yet \u2014 this condition matches nothing until you set one."
    };
  }
  if (isNumericField(predicate2.field)) {
    const values = Array.isArray(predicate2.value) ? predicate2.value : [predicate2.value];
    const text = values.find((v) => v !== "" && v != null && typeof v !== "number");
    if (text !== void 0) {
      const label = getTriggerField(predicate2.field)?.label ?? predicate2.field;
      return {
        kind: "numeric-mismatch",
        tone: "error",
        message: `${label} expects a number \u2014 \u201C${valueText(text)}\u201D will be compared as text.`
      };
    }
  }
  return null;
}

export { DEFAULT_CONNECTORS, ENRICHMENT_KINDS, EVENT_KINDS, FINALITY_STATES, LogicalOp, OPERATORS_BY_TYPE, RISK_BUCKETS, SIGNAL_KINDS, TRIGGER_ACTION_TYPES, TRIGGER_ACTION_TYPE_LABELS, TRIGGER_FIELDS, TRIGGER_FIELD_GROUP_LABELS, TriggerActionType, and, anyCondition, coerceValue, collectEnrichments, compare, conditionFromPredicates, emptyTriggerRule, evaluateCondition, evaluatePredicate, evaluateRule, flattenCondition, getCanonicalField, getConnector, getTriggerField, integrationAction, isIntegrationAction, isNotifyAction, isStartWorkflowAction, not, notifyAction, operatorsForField, or, parseTriggerAction, predicate, predicateLeaf, predicateProblem, previewRule, startWorkflowAction, triggerActionToRecord, triggerFieldLabel, triggerFieldOptions, valueText };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map